import { useTranslation } from '@/app/_shared/hooks/useTranslation';
import type React from 'react';
import { useState } from 'react';
import { Button } from '../Button';
import { SearchNormalIcon } from '../Icons';
import Input from '../Input/Input';
import { SelectionItem } from '../SelectionItem';

interface Item {
	id: string;
	image?: string;
	label: string;
}

interface SelectionPageProps {
	title: string;
	items: Item[];
	type: 'checkbox' | 'radio';
	initialSelected?: string | string[];
	onSave: (selected: string | string[]) => void;
	searchPlaceholder?: string;
}

export const SelectionPage: React.FC<SelectionPageProps> = ({
	title,
	items,
	type,
	initialSelected = type === 'radio' ? '' : [],
	onSave,
	searchPlaceholder = 'Search...',
}) => {
	const [selected, setSelected] = useState<string | string[]>(initialSelected);
	const [searchQuery, setSearchQuery] = useState('');
	const { t } = useTranslation('common');

	const handleSelect = (id: string) => {
		if (type === 'radio') {
			setSelected(id);
		} else {
			setSelected((prev) => {
				const prevArray = prev as string[];
				if (prevArray.includes(id)) {
					return prevArray.filter((item) => item !== id);
				}
				return [...prevArray, id];
			});
		}
	};

	const filteredItems = items.filter((item) =>
		item.label.toLowerCase().includes(searchQuery.toLowerCase()),
	);

	const isSelected = (id: string) => {
		if (type === 'radio') {
			return selected === id;
		}
		return (selected as string[]).includes(id);
	};

	return (
		<div className="pt-12.5 px-9">
			<Input
				type="text"
				placeholder={searchPlaceholder}
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.target.value)}
				prefixIcon={SearchNormalIcon}
				inputClassName="!border-none !bg-grey-4 !h-10"
				style={{ borderRadius: '0.625rem' }}
			/>
			<div className="text-sm text-secondary-black font-medium my-3.75">
				{title}
			</div>

			<div className="mb-8.5 grid gap-y-2.5">
				{filteredItems.map((item) => (
					<SelectionItem
						key={item.id}
						image={item.image}
						label={item.label}
						selected={isSelected(item.id)}
						onClick={() => handleSelect(item.id)}
					/>
				))}
			</div>

			<Button
				type="button"
				variant="primary"
				className="w-full h-10 font-bold"
				disabled={
					!selected || (Array.isArray(selected) && selected.length === 0)
				}
				onClick={() => onSave(selected)}
			>
				{t('common:saveChanges')}
			</Button>
		</div>
	);
};
